import { TFunction } from 'i18next';

import { Assignable } from 'domains-work-order-type-contracts';
import { getDisplayName } from 'lane-shared/helpers';
import { UserType } from 'lane-shared/types/User';
import { convertTo62 } from 'uuid-encoding';

import { ClientTask } from 'graphql-query-contracts';

type BuildAssigneeOptionsParams = {
  task?: ClientTask;
  user: UserType | null;
  assignableUsers: Assignable[];
  canAssignIndividual: boolean;
  canBeAssigned: boolean;
  t: TFunction;
};

function getCurrentUserId(user: UserType | null) {
  return user?._id ? convertTo62(user?._id) : '';
}

function buildUnassignOption(t: TFunction) {
  return {
    label: t`web.admin.workOrder.shared.unassign`,
    value: 'unassign',
    displayName: t`web.admin.workOrder.shared.unassign`,
  };
}

function buildAssignMyselfOption(t: TFunction, currentUserId: string) {
  return {
    label: t`web.admin.workOrder.preventiveMaintenance.task.details.assignMyself`,
    value: currentUserId,
    displayName: t`web.admin.workOrder.preventiveMaintenance.task.details.assignMyself`,
  };
}

function buildCurrentUserOption(user: UserType | null, currentUserId: string) {
  return {
    label: getDisplayName(user),
    value: currentUserId,
    displayName: getDisplayName(user),
  };
}

function findCurrentlyAssigned(
  task: ClientTask | undefined,
  assignableUsers: Assignable[]
) {
  return task?.assignee && task?.assignee?._id
    ? assignableUsers.filter(
        assignee => assignee.value === convertTo62(task?.assignee?._id)
      )
    : [];
}

function canAssignMyself(
  task: ClientTask | undefined,
  user: UserType | null,
  canBeAssigned: boolean
) {
  return (
    canBeAssigned && (!task?.assignee?._id || task?.assignee?._id !== user?._id)
  );
}

export function buildAssigneeOptions({
  task,
  user,
  assignableUsers,
  canAssignIndividual,
  canBeAssigned,
  t,
}: BuildAssigneeOptionsParams) {
  const currentUserId = getCurrentUserId(user);
  const unAssignOption = buildUnassignOption(t);
  const assignMyselfOption = buildAssignMyselfOption(t, currentUserId);
  const currentUserOption = buildCurrentUserOption(user, currentUserId);
  const currentlyAssigned = findCurrentlyAssigned(task, assignableUsers);

  const isCurrentUserExist = assignableUsers.some(
    user => user.value === assignMyselfOption.value
  );

  const assigneeOptions = [
    ...(!canAssignIndividual && task?.assignee ? currentlyAssigned : []),
    ...(task?.assignee ? [unAssignOption] : []),
    ...(canAssignMyself(task, user, canBeAssigned) ? [assignMyselfOption] : []),
    ...(canAssignIndividual ? assignableUsers : []),
  ];

  if (canBeAssigned && !isCurrentUserExist) {
    return {
      assigneeOptions: [...assigneeOptions, currentUserOption],
      assignableUsersWithCurrentUser: [...assignableUsers, currentUserOption],
    };
  }

  return { assigneeOptions, assignableUsersWithCurrentUser: assignableUsers };
}
